import React, { useState } from "react"
import PropTypes from "prop-types"
import Chart from "react-apexcharts";
import { formatAmount, formatTimestamp } from '../utils'


const fraudDisplay = (source) => {
  const [modalActive, setModalActive] = useState(false);

  if (source.fraud_analysis.length == 0) return null;

  return(
    <>
      <a className="tag is-danger" onClick={() => setModalActive(true)}>Issues Detected</a>

      <div className={"modal " + (modalActive && 'is-active')}>
        <div className="modal-background" onClick={() => setModalActive(false)}></div>

        <div className="modal-content">
          <div className="box">
            <p>{source.name}</p>
            <ul style={{ textTransform: 'none' }}>
              {source.fraud_analysis.map((f) => <li>{f}</li>)}
            </ul>
          </div>
        </div>

        <button onClick={() => setModalActive(false)} className="modal-close is-large" aria-label="close"></button>
      </div>
    </>
  )
  
}

const confidenceScore = (score) => {
  if (score == null || score === '') return (
    <span className="icon-text has-text-danger">
      <span className="icon">
        <i className="fas fa-ban"></i>
      </span>
    </span>
  );
  if (score == 100){
    return <span className="icon-text has-text-success">
            <span className="icon">
              <i className="fas fa-check-square"></i>
            </span>
          </span>
  } else if (score >= 50){
    return <span className="icon-text has-text-warning">
            <span className="icon">
              <i className="fas fa-exclamation-triangle"></i>
            </span>
          </span>
  } else {
    return <span className="icon-text has-text-danger">
              <span className="icon">
                <i className="fas fa-ban"></i>
              </span>
            </span>
  }
}

const statusFromScore = (score) => {
  if (score == null || score === '') return 'red'
  const numeric = Number(score)
  if (numeric === 100) return 'green'
  if (numeric >= 50) return 'yellow'
  return 'red'
}

const renderNoteBody = (body) => {
  if (!body) return null
  const lines = body.split(/\r?\n/)
  return lines.map((line, idx) => (
    <React.Fragment key={`${line}-${idx}`}>
      {line}
      {idx < lines.length - 1 && <br />}
    </React.Fragment>
  ))
}

const FundsVerificationOverview = ({ summary, currency, allZero }) => {
  const parseSeriesAmount = (amounts) => {
    return amounts.map((amount) => {
      if (amount == 0){
        return amount
      }
      return amount[currency]
    })
  }

  const series = summary.sources.map((source) => { 
    return {
      name: source.name,
      data: parseSeriesAmount( Object.values(source.last_6_months) )
    }
  });

  const currencyDisplay = currency || '';
  let data = {   
      series: series,
      options: {
        colors:['#1F295B', '#EAAF0C', '#0b0047', '#a32047', '#5b2d05', '#47a320'],
        chart: {
          type: 'bar',
          height: 350,
           toolbar: {
            show: false,
          }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded'
          },
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent']
        },
        xaxis: {
          categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        },
        yaxis: {
          title: {
            text: currencyDisplay
          }
        },
        fill: {
          opacity: 1
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return formatAmount(val, currency)
            }
          }
        }
      },
    
    };

  const totals = summary.sources.reduce((acc, source) => {
    const amount = source.balance?.[currency] || 0
    const status = statusFromScore(source.confidence_score)
    if (status === 'green') acc.green += amount
    else if (status === 'yellow') acc.yellow += amount
    else acc.red += amount
    return acc
  }, { green: 0, yellow: 0, red: 0 })

  const statusIcon = (status) => {
    const map = {
      green: { className: 'has-text-success', icon: 'fa-check-square' },
      yellow: { className: 'has-text-warning', icon: 'fa-exclamation-triangle' },
      red: { className: 'has-text-danger', icon: 'fa-ban' }
    }
    const cfg = map[status] || map.red
    return (
      <span className={`icon ${cfg.className}`}>
        <i className={`fas ${cfg.icon}`} aria-hidden="true"></i>
      </span>
    )
  }

  const includeGreen = summary.include_green_total !== false
  const includeGreenYellow = summary.include_green_yellow_total !== false
  const combinedAmount = totals.green + totals.yellow
  const latestNote = summary.latest_counselor_note

  const latestTimestamp = summary.last_synced_at_timestamp
    ? formatTimestamp(summary.last_synced_at_timestamp)
    : (summary.last_synced_at || null)

  return (
    <div id="funds-verification-overview">
      <div className="columns">
        <div className="column is-two-thirds pr-4">
          <div className="card" style={{ height: '100%', minHeight: '420px', display: 'flex', flexDirection: 'column' }}>
            <div className="card-content" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div className="content">
                <h4 style={{ fontSize: '1rem' }}>Most Recent Accounts as of {latestTimestamp || 'N/A'}</h4>
                <div style={{ backgroundColor: "#0b0047", color: "white", padding: "5px", borderRadius: "5px", display: "flex", flexWrap: "wrap", gap: "1rem" }}>
                  <div className="m-1">Key: Ending Balance / Period / Bank / Account Type / Account Name / Retrieval Timestamp</div>
                </div>
              <ol className="is-capitalized">
                {summary.sources.map((source) => {
                  const acctDigitsRaw = (source.account_last_digits || source.account_number_last_digits || '').toString().trim();
                  const accountLabel = [(source.account_type || '').toUpperCase(), acctDigitsRaw].filter((v) => v && v.length).join('-');
                  const bankName = source.bank || source.name || 'N/A';
                  const holder = source.account_holder_name || summary.external_identifier || 'N/A';
                  const displayCurrency = (currency || source.currency || 'USD').toUpperCase();
                  const amountValue = (source.balance && (source.balance[displayCurrency] ?? source.balance[source.currency])) || 0;
                  const amount = formatAmount(amountValue, displayCurrency);
                  const period = source.period || 'N/A';
                  const timestamp = source.captured_at_formatted || summary.last_synced_at || 'N/A';
                  const tooltip = `\u202A${amount} / ${period} / ${bankName} / ${(accountLabel && accountLabel.length ? accountLabel : 'N/A')} / ${holder} / ${timestamp}\u202C`;
                  return (
                    <li
                      className='has-text-weight-normal'
                      key={source.id}
                      style={{ textTransform: 'none' }}
                      title={tooltip}
                      >
                      {confidenceScore(source.confidence_score)}
                      <span dir="ltr">
                        {amount} / {period} / {bankName} / {(accountLabel && accountLabel.length ? accountLabel : 'N/A')} /{' '}
                      </span>
                      <span dir="rtl" style={{ unicodeBidi: 'isolate' }}>
                        {holder}
                      </span>
                      <span dir="ltr"> / {timestamp}</span>
                      {' '}
                      {fraudDisplay(source)}
                    </li>
                  );
                })}
              </ol>
            </div>

              {latestNote && (
                <div
                  style={{
                    marginTop: 'auto',
                    paddingTop: '1rem',
                    borderTop: '1px solid #e5e7eb'
                  }}
                >
                  <h3
                    style={{
                      fontSize: '1rem',
                      letterSpacing: '0.05em',
                      fontWeight: 600,
                      color: '#111827',
                      marginBottom: '0.25rem'
                    }}
                  >
                    COUNSELOR NOTES
                  </h3>
                  {latestNote.created_at_formatted && (
                    <p
                      style={{
                        fontSize: '0.85rem',
                        color: '#6b7280',
                        marginBottom: '0.25rem'
                      }}
                    >
                      {latestNote.created_at_formatted}
                    </p>
                  )}
                  <p
                    style={{
                      fontSize: '0.95rem',
                      color: '#1f2937',
                      lineHeight: 1.4,
                      margin: 0
                    }}
                  >
                    {renderNoteBody(latestNote.body)}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="column is-one-third pr-4">
          <div className="card" style={{ height: '100%', minHeight: '420px', display: 'flex', flexDirection: 'column' }}>
            <div className="card-content" style={{ flex: 1 }}>
              <div
                className="content total-funds-list"
                style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
              >
                <h4 style={{ fontSize: '1rem' }}>Sum of Ending Balances</h4>
                <p className="total-funds-note">
                  *See <a href="https://fundsvalidator.com/faq/#icons" target="_blank" rel="noopener noreferrer">FundsV.com</a> for Icon Explanation
                </p>
                {[{key:'green', label:'High Confidence', value: totals.green},
                  {key:'yellow', label:'Anomalies Detected', value: totals.yellow},
                  {key:'red', label:'Low Confidence', value: totals.red}
                ].map(item => (
                  <div
                    key={item.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.35rem'
                    }}
                  >
                    {statusIcon(item.key)}
                    <span style={{ flex: 1 }}>{item.label}</span>
                    <span style={{ fontWeight: 600 }}>{formatAmount(item.value, currency)}</span>
                  </div>
                ))}
              </div>

              <div
                className="total-funds-selection"
                style={{
                  marginTop: '1rem',
                  paddingTop: '0.75rem',
                  borderTop: '1px solid #e5e7eb'
                }}
              >
                <h3>Included in Total Funds</h3>
                {(includeGreen || (!includeGreen && !includeGreenYellow)) && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      width: '100%',
                      justifyContent: 'space-between',
                      marginTop: '0.5rem'
                    }}
                  >
                    {statusIcon('green')}
                    <span style={{ flex: 1 }}></span>
                    <span style={{ fontWeight: 600 }}>{formatAmount(totals.green, currency)}</span>
                  </div>
                )}
                {includeGreenYellow && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      width: '100%',
                      justifyContent: 'space-between',
                      marginTop: '0.5rem'
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      {statusIcon('green')}+
                      {statusIcon('yellow')}
                    </span>
                    <span style={{ flex: 1 }}></span>
                    <span style={{ fontWeight: 600 }}>{formatAmount(combinedAmount, currency)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* {!allZero &&
        <div className="card">
          <div className="card-content">
            <div className="content">
              <h2>Monthly Balances</h2>
              <div id="chart">
                <Chart options={data.options} series={data.series} type="bar" height={350} />
              </div>
            </div>
          </div>
        </div>
      } */}
    </div>
  );
}


FundsVerificationOverview.propTypes = {
  summary: PropTypes.object,
  currency: PropTypes.string
};
export default FundsVerificationOverview
